import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import GrowthBadge from '@/components/GrowthBadge';
import { useUser } from '@/context/UserContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, MapPin, Phone, Mail, Linkedin, Plus, Trash2, ArrowLeft, Smartphone, PhoneCall, Pencil, Check, X, Tag, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { ACTION_COLORS, ETAT_COLORS, LODEOM_COLORS } from './Dashboard';

const ACTIONS = ["A appeler", "A rappeler", "RDV pris", "RDV realise"];
const ETATS_COMPTE = ["A renseigner", "Propale en negociation", "Perdu", "Concu", "RAF", "Client RH", "Client FIPRO", "Client PMS", "A renouveler"];

const InitialsBadge = ({ initials }) => {
  if (!initials) return null;
  return (
    <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded bg-primary/10 text-primary text-[9px] font-bold ml-1.5" data-testid="initials-badge">
      {initials}
    </span>
  );
};

const ContactInfoField = ({ contact, field, label, icon: Icon, onSave, readOnly }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const currentValue = contact[field];
  const addedBy = contact[`${field}_added_by`];

  const handleSave = () => {
    if (value.trim()) {
      onSave(contact.id, field, value.trim());
    }
    setEditing(false);
    setValue('');
  };

  if (editing && !readOnly) {
    return (
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={label}
          className="h-7 text-xs flex-1"
          autoFocus
          onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setEditing(false); }}
          data-testid={`edit-${field}-input`}
        />
        <button onClick={handleSave} className="text-green-600 hover:text-green-700" data-testid={`save-${field}-btn`}><Check className="h-3.5 w-3.5" /></button>
        <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
      </div>
    );
  }

  if (currentValue) {
    return (
      <div className="flex items-center gap-2 text-sm group">
        <Icon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        <span className="truncate">{currentValue}</span>
        <InitialsBadge initials={addedBy} />
        {!readOnly && (
        <button onClick={() => { setEditing(true); setValue(currentValue); }} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`edit-${field}-btn`}>
          <Pencil className="h-3 w-3 text-muted-foreground" />
        </button>
        )}
      </div>
    );
  }

  if (readOnly) return null;

  return (
    <button
      onClick={() => setEditing(true)}
      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors"
      data-testid={`add-${field}-btn`}
    >
      <Icon className="h-3 w-3" />
      <span>+ {label}</span>
    </button>
  );
};

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canDeleteContacts } = useUser();
  const [company, setCompany] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [companyCampaigns, setCompanyCampaigns] = useState([]);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [nafDescriptions, setNafDescriptions] = useState({});
  const [capitalLinks, setCapitalLinks] = useState([]);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkSearch, setLinkSearch] = useState('');
  const [linkSearchResults, setLinkSearchResults] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [linkType, setLinkType] = useState('');
  const [linkPercentage, setLinkPercentage] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    position: '',
    email: '',
    phone: '',
    mobile: '',
    linkedin_url: '',
    source: 'Manuel',
  });

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [companyRes, contactsRes, notesRes, campaignsRes, tagsRes] = await Promise.all([
        axios.get(`${API}/companies/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contacts?company_id=${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/notes?entity_type=company&entity_id=${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/companies/${id}/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tags`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      setCompany(companyRes.data);
      setContacts(contactsRes.data);
      setNotes(notesRes.data);
      setCompanyCampaigns(campaignsRes.data);
      setAllTags(tagsRes.data);

      // Fetch capital links
      try {
        const linksRes = await axios.get(`${API}/companies/${id}/capital-links`, { headers: { Authorization: `Bearer ${token}` } });
        setCapitalLinks(linksRes.data);
      } catch (e) { /* silent */ }

      // Fetch NAF descriptions if not loaded yet
      if (Object.keys(nafDescriptions).length === 0) {
        try {
          const nafRes = await axios.get(`${API}/naf/descriptions`, { headers: { Authorization: `Bearer ${token}` } });
          setNafDescriptions(nafRes.data);
        } catch (e) { /* silent */ }
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/contacts`, { ...contactForm, company_id: id }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Contact ajouté avec succès');
      setIsContactDialogOpen(false);
      fetchData();
      setContactForm({
        name: '',
        position: '',
        email: '',
        phone: '',
        mobile: '',
        linkedin_url: '',
        source: 'Manuel',
      });
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du contact');
    }
  };

  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/notes`,
        { entity_type: 'company', entity_id: id, content: newNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Note ajoutée');
      setNewNote('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout de la note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/notes/${noteId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Note supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteCompany = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entreprise ?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/companies/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Entreprise supprimée');
      navigate('/');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateContactInfo = async (contactId, field, value) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      payload[field] = value;
      await axios.patch(`${API}/contacts/${contactId}/info`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Info mise à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Supprimer ce contact ?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/contacts/${contactId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Contact supprime');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleStatusUpdate = async (field, value) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {};
      payload[field] = value;
      const res = await axios.patch(`${API}/companies/${id}/status`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompany(res.data);
      toast.success(`${field === 'action' ? 'Action' : 'Etat du compte'} mis a jour`);
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const handleAddTag = async (tag) => {
    const t = (tag || tagInput).trim();
    if (!t) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/companies/${id}/tags`, { tag: t }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompany(prev => ({ ...prev, tags: res.data.tags }));
      setTagInput('');
      if (!allTags.includes(t)) setAllTags(prev => [...prev, t].sort());
      toast.success(`Tag "${t}" ajouté`);
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du tag');
    }
  };

  const handleRemoveTag = async (tag) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/companies/${id}/tags/${encodeURIComponent(tag)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompany(prev => ({ ...prev, tags: res.data.tags }));
      toast.success(`Tag "${tag}" retiré`);
    } catch (error) {
      toast.error('Erreur lors de la suppression du tag');
    }
  };

  const searchCompanyForLink = async (q) => {
    if (!q || q.length < 2) { setLinkSearchResults([]); return; }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/companies/search?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLinkSearchResults(res.data.filter(c => c.id !== id));
    } catch (e) { /* silent */ }
  };

  const handleAddCapitalLink = async () => {
    if (!selectedTarget || !linkType) {
      toast.error('Selectionnez une entreprise et un type de lien');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/companies/${id}/capital-links`, {
        target_company_id: selectedTarget.id,
        link_type: linkType,
        percentage: linkPercentage ? parseFloat(linkPercentage) : null,
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.status === 'validated') {
        setCapitalLinks(prev => [...prev, res.data]);
        toast.success('Lien capitalistique ajoute');
      } else {
        toast.success('Demande de lien soumise - en attente de validation');
      }
      setIsLinkDialogOpen(false);
      setSelectedTarget(null);
      setLinkType('');
      setLinkPercentage('');
      setLinkSearch('');
      setLinkSearchResults([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/capital-links/${linkId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCapitalLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success('Lien supprime');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (!company) {
    return (
      <div className="min-h-screen bg-[#f1f5f9]">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  const positions = ['DG', 'PDG', 'DRH', 'DAF', 'Directeur des Opérations', 'Directeur'];

  return (
    <div className="min-h-screen bg-[#f1f5f9]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 gap-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour au dashboard
        </Button>

        <div className="bg-white rounded-xl border border-border/50 shadow-sm p-8 mb-6" data-testid="company-detail">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-accent rounded-xl">
                <Building2 className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-heading font-bold text-gray-900" data-testid="company-name">
                  {company.name}
                  {company.situation_juridique && company.situation_juridique !== 'Actif' && (
                    <span className="text-red-500 text-lg font-bold ml-1" data-testid="situation-indicator">*</span>
                  )}
                </h1>
                <p className="text-muted-foreground">SIREN: {company.siren}</p>
                {company.situation_juridique && company.situation_juridique !== 'Actif' && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200" data-testid="situation-banner">
                    <span className="text-red-500 font-bold text-sm">*</span>
                    <span className="text-red-700 text-sm font-medium">{company.situation_juridique}</span>
                    {company.situation_details && <span className="text-red-500 text-xs">- {company.situation_details}</span>}
                    {company.situation_date && <span className="text-red-400 text-xs">({company.situation_date})</span>}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {company.growth_badge && <GrowthBadge badge={company.growth_badge} />}
              {canEdit && (
              <Button
                variant="destructive"
                onClick={handleDeleteCompany}
                className="gap-2"
                data-testid="delete-company-btn"
              >
                <Trash2 className="h-4 w-4" />
                Supprimer
              </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-heading font-semibold mb-3">Informations générales</h3>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm text-muted-foreground">Effectif</dt>
                  <dd className="font-medium">
                    {company.size} employés
                    {company.source === 'Import Excel' && company.tranche_effectif && 
                      <span className="text-sm text-muted-foreground ml-2">(Tranche {company.tranche_effectif})</span>
                    }
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Secteur (NAF)</dt>
                  <dd className="font-medium">{company.sector}</dd>
                  {company.sector && nafDescriptions[company.sector] && (
                    <dd className="text-xs text-muted-foreground mt-0.5">{nafDescriptions[company.sector]}</dd>
                  )}
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Statut juridique</dt>
                  <dd className="font-medium">{company.legal_status || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Date de création</dt>
                  <dd className="font-medium">{company.creation_date || 'N/A'}</dd>
                </div>
              </dl>
            </div>

            <div>
              <h3 className="font-heading font-semibold mb-3">Localisation</h3>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm text-muted-foreground">Région</dt>
                  <dd className="font-medium">{company.region}</dd>
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Ville</dt>
                  <dd className="font-medium">{company.city}</dd>
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Code postal</dt>
                  <dd className="font-medium">{company.postal_code}</dd>
                </div>
                <div>
                  <dt className="text-sm text-muted-foreground">Adresse</dt>
                  <dd className="font-medium">{company.address}</dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Campagnes */}
          {companyCampaigns.length > 0 && (
            <div className="mt-6 pt-6 border-t border-border/50">
              <h3 className="font-heading font-semibold mb-3">Campagnes</h3>
              <div className="flex flex-wrap gap-2" data-testid="company-campaigns">
                {companyCampaigns.map(c => (
                  <span key={c.id} className="text-xs font-semibold px-3 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20">
                    {c.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="mt-6 pt-6 border-t border-border/50">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Tags
            </h3>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="company-tags">
              {(company.tags || []).map(tag => (
                <span key={tag} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200" data-testid="tag-badge">
                  {tag}
                  {canEdit && (
                    <button onClick={() => handleRemoveTag(tag)} className="hover:text-red-600 transition-colors" data-testid="remove-tag-btn">
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </span>
              ))}
              {(!company.tags || company.tags.length === 0) && (
                <span className="text-xs text-muted-foreground">Aucun tag</span>
              )}
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <div className="relative flex-1 max-w-xs">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Nouveau tag..."
                    className="h-8 text-sm pr-8"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTag(); } }}
                    list="tag-suggestions"
                    data-testid="tag-input"
                  />
                  <datalist id="tag-suggestions">
                    {allTags.filter(t => !(company.tags || []).includes(t)).map(t => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                </div>
                <Button size="sm" onClick={() => handleAddTag()} className="h-8 rounded-full gap-1" data-testid="add-tag-btn">
                  <Plus className="h-3 w-3" />
                  Ajouter
                </Button>
              </div>
            )}
          </div>

          {/* Liens capitalistiques */}
          <div className="mt-6 pt-6 border-t border-border/50">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold flex items-center gap-2">
                <Link2 className="h-4 w-4" />
                Liens capitalistiques
              </h3>
              <Dialog open={isLinkDialogOpen} onOpenChange={(open) => { setIsLinkDialogOpen(open); if (!open) { setSelectedTarget(null); setLinkType(''); setLinkPercentage(''); setLinkSearch(''); setLinkSearchResults([]); } }}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="gap-1 rounded-full" data-testid="add-capital-link-btn">
                    <Plus className="h-3 w-3" />
                    Lier
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Ajouter un lien capitalistique</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {!selectedTarget ? (
                      <>
                        <div>
                          <Label>Rechercher l'entreprise cible</Label>
                          <Input
                            value={linkSearch}
                            onChange={(e) => { setLinkSearch(e.target.value); searchCompanyForLink(e.target.value); }}
                            placeholder="Nom ou SIREN..."
                            data-testid="link-search-input"
                          />
                        </div>
                        {linkSearchResults.length > 0 && (
                          <div className="space-y-1 max-h-[200px] overflow-y-auto" data-testid="link-search-results">
                            {linkSearchResults.map(c => (
                              <div
                                key={c.id}
                                onClick={() => { setSelectedTarget(c); setLinkSearchResults([]); }}
                                className="p-2 rounded-lg border border-border hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer transition-colors"
                              >
                                <p className="text-sm font-semibold">{c.name}</p>
                                <p className="text-xs text-muted-foreground">{c.siren} {c.region && `- ${c.region}`}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-sm font-semibold">{selectedTarget.name}</p>
                          <p className="text-xs text-muted-foreground">SIREN: {selectedTarget.siren}</p>
                          <button onClick={() => setSelectedTarget(null)} className="text-xs text-blue-600 underline mt-1">Changer</button>
                        </div>

                        <div>
                          <Label>Type de lien *</Label>
                          <Select value={linkType} onValueChange={setLinkType}>
                            <SelectTrigger data-testid="link-type-select">
                              <SelectValue placeholder="Choisir le type..." />
                            </SelectTrigger>
                            <SelectContent>
                              {["Maison mère", "Filiale", "Actionnaire", "Participation"].map(t => (
                                <SelectItem key={t} value={t}>{t}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>% de detention (optionnel)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={linkPercentage}
                            onChange={(e) => setLinkPercentage(e.target.value)}
                            placeholder="Ex: 100"
                            data-testid="link-percentage-input"
                          />
                        </div>

                        <Button onClick={handleAddCapitalLink} className="w-full bg-[#1A4D2E] text-white hover:bg-[#153d24] rounded-full" data-testid="confirm-link-btn">
                          Soumettre le lien
                        </Button>
                      </>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {capitalLinks.length > 0 ? (
              <div className="space-y-2" data-testid="capital-links-list">
                {capitalLinks.map(link => {
                  const isSource = link.source_company_id === id;
                  const linkedName = isSource ? link.target_company_name : link.source_company_name;
                  const linkedId = isSource ? link.target_company_id : link.source_company_id;
                  const linkedSiren = isSource ? link.target_company_siren : link.source_company_siren;
                  const displayType = isSource ? link.link_type : (
                    link.link_type === "Maison mère" ? "Filiale de" :
                    link.link_type === "Filiale" ? "Maison mère de" :
                    link.link_type
                  );
                  return (
                    <div key={link.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-slate-50/50 group">
                      <div className="flex items-center gap-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-indigo-100 text-indigo-700 border border-indigo-200">{displayType}</span>
                        <div>
                          <button
                            onClick={() => navigate(`/companies/${linkedId}`)}
                            className="text-sm font-semibold text-primary hover:underline"
                            data-testid="linked-company-name"
                          >
                            {linkedName}
                          </button>
                          <p className="text-xs text-muted-foreground">
                            SIREN: {linkedSiren}
                            {link.percentage && ` - ${link.percentage}%`}
                          </p>
                        </div>
                      </div>
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteLink(link.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          data-testid="delete-link-btn"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Aucun lien capitalistique enregistre</p>
            )}
          </div>

          {/* Action & Etat du compte */}
          <div className="mt-6 pt-6 border-t border-border/50">
            <h3 className="font-heading font-semibold mb-4">Suivi commercial</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Label className="text-sm text-muted-foreground">Action</Label>
                  {company.action_updated_by && <InitialsBadge initials={company.action_updated_by} />}
                </div>
                {canEdit ? (
                <Select
                  value={company.action || 'A appeler'}
                  onValueChange={(v) => handleStatusUpdate('action', v)}
                >
                  <SelectTrigger data-testid="action-select" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map(a => (
                      <SelectItem key={a} value={a}>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded border ${ACTION_COLORS[a] || ''}`}>{a}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                ) : (
                  <span className={`text-xs font-medium px-2.5 py-1 rounded border ${ACTION_COLORS[company.action || 'A appeler'] || ''}`}>{company.action || 'A appeler'}</span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Label className="text-sm text-muted-foreground">Etat du compte</Label>
                  {company.etat_updated_by && <InitialsBadge initials={company.etat_updated_by} />}
                </div>
                {canEdit ? (
                <Select
                  value={company.etat_compte || 'A renseigner'}
                  onValueChange={(v) => handleStatusUpdate('etat_compte', v)}
                >
                  <SelectTrigger data-testid="etat-compte-select" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ETATS_COMPTE.map(e => (
                      <SelectItem key={e} value={e}>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded border ${ETAT_COLORS[e] || ''}`}>{e}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                ) : (
                  <span className={`text-xs font-medium px-2.5 py-1 rounded border ${ETAT_COLORS[company.etat_compte || 'A renseigner'] || ''}`}>{company.etat_compte || 'A renseigner'}</span>
                )}
              </div>
            </div>
          </div>

          {/* Bareme LODEOM */}
          <div className="mt-6 pt-6 border-t border-border/50">
            <h3 className="font-heading font-semibold mb-4">Bareme LODEOM</h3>
            <div className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border ${LODEOM_COLORS[company.bareme_lodeom] || 'bg-gray-50 text-gray-400 border-gray-200'}`}>
              <span className="text-sm font-semibold">{company.bareme_lodeom || 'HL'}</span>
              {company.bareme_lodeom_alt && <span className="text-sm">*</span>}
            </div>
            {company.bareme_lodeom_alt && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <strong>* Double eligibilite :</strong> Ce code NAF ({company.sector}) est egalement eligible au bareme <strong>"{company.bareme_lodeom_alt}"</strong>. Le bareme le plus avantageux ({company.bareme_lodeom}) est affiche par defaut.
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Calcule automatiquement selon le code NAF. Non modifiable.
            </p>
          </div>

          <div className="mt-6 pt-6 border-t border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <span className="text-sm text-muted-foreground">Source: </span>
                <span className="text-sm font-medium">{company.source}</span>
              </div>
              {company.added_by && (
                <div>
                  <span className="text-xs text-muted-foreground">Ajoute par: </span>
                  <span className="text-xs font-medium text-muted-foreground">{company.added_by}</span>
                </div>
              )}
            </div>
            <div>
              <span className="text-sm px-3 py-1 bg-accent/50 rounded-full text-primary font-medium">
                Fiabilité: {company.reliability}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-border/50 shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-heading font-semibold">Contacts</h2>
              {canEdit && (
              <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="gap-2 rounded-full" data-testid="add-contact-btn">
                    <Plus className="h-4 w-4" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-heading">Nouveau contact</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddContact} className="space-y-4">
                    <div>
                      <Label htmlFor="contact_name">Nom *</Label>
                      <Input
                        id="contact_name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="position">Fonction *</Label>
                      <Select
                        value={contactForm.position}
                        onValueChange={(value) => setContactForm({ ...contactForm, position: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une fonction" />
                        </SelectTrigger>
                        <SelectContent>
                          {positions.map((pos) => (
                            <SelectItem key={pos} value={pos}>
                              {pos}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Téléphone</Label>
                      <Input
                        id="phone"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="mobile">Mobile</Label>
                      <Input
                        id="mobile"
                        value={contactForm.mobile}
                        onChange={(e) => setContactForm({ ...contactForm, mobile: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="linkedin_url">LinkedIn</Label>
                      <Input
                        id="linkedin_url"
                        value={contactForm.linkedin_url}
                        onChange={(e) => setContactForm({ ...contactForm, linkedin_url: e.target.value })}
                      />
                    </div>
                    <Button type="submit" className="w-full rounded-full">
                      Ajouter le contact
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              )}
            </div>

            <div className="space-y-4" data-testid="contacts-list">
              {contacts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucun contact</p>
              ) : (
                contacts.map((contact) => (
                  <div
                    key={contact.id}
                    className="border border-border/50 rounded-lg p-4 hover:bg-accent/20 transition-colors"
                    data-testid="contact-card"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div
                        className="cursor-pointer"
                        onClick={() => navigate(`/contacts/${contact.id}`)}
                      >
                        <h4 className="font-semibold text-gray-900 hover:text-primary transition-colors">{contact.name}</h4>
                        <p className="text-sm text-muted-foreground">{contact.position}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {contact.linkedin_url && (
                          <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary">
                            <Linkedin className="h-4 w-4" />
                          </a>
                        )}
                        {canDeleteContacts && (
                          <button
                            onClick={() => handleDeleteContact(contact.id)}
                            className="text-red-400 hover:text-red-600 transition-colors"
                            data-testid="delete-contact-btn"
                            title="Supprimer ce contact"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5 mt-2">
                      <ContactInfoField contact={contact} field="email" label="Mail" icon={Mail} onSave={handleUpdateContactInfo} readOnly={!canEdit} />
                      <ContactInfoField contact={contact} field="phone" label="Tel fixe" icon={PhoneCall} onSave={handleUpdateContactInfo} readOnly={!canEdit} />
                      <ContactInfoField contact={contact} field="mobile" label="Tel portable" icon={Smartphone} onSave={handleUpdateContactInfo} readOnly={!canEdit} />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-border/50 shadow-sm p-6">
            <h2 className="text-xl font-heading font-semibold mb-6">Notes</h2>

            {canEdit && (
            <form onSubmit={handleAddNote} className="mb-6">
              <Textarea
                placeholder="Ajouter une note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="mb-2"
                data-testid="note-input"
              />
              <Button type="submit" className="rounded-full" data-testid="add-note-btn">
                Ajouter la note
              </Button>
            </form>
            )}

            <div className="space-y-3" data-testid="notes-list">
              {notes.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">Aucune note</p>
              ) : (
                notes.map((note) => (
                  <div key={note.id} className="border border-border/50 rounded-lg p-4" data-testid="note-item">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {note.author_initials && (
                          <span className="shrink-0 h-7 w-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center" title={note.author_name || ''} data-testid="note-author-initials">
                            {note.author_initials}
                          </span>
                        )}
                        <p className="text-sm text-gray-700 flex-1">{note.content}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="text-destructive hover:text-destructive/80 ml-2"
                        data-testid="delete-note-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {note.author_name && <span className="font-medium">{note.author_name} &middot; </span>}
                      {new Date(note.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetail;